import type { CSSProperties, ReactNode } from 'react';

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ LAYOUT CANÓNICO — quem é o dono de cada espaço.                           ║
// ║                                                                          ║
// ║ O problema que isto resolve não era estético, era de PROPRIEDADE. O ritmo ║
// ║ vertical estava no padding de cada `.section` (20px em cima), e 49 dos    ║
// ║ 126 usos da app escreviam `style={{paddingTop:0}}` para o desligar. O     ║
// ║ resultado: o espaço entre dois blocos era a soma acidental do que cada    ║
// ║ um deles decidiu — 8px aqui, 14px ali, 0 acolá, no MESMO ecrã.            ║
// ║                                                                          ║
// ║ Aqui o espaço tem um dono só:                                            ║
// ║   • Page    → o espaço ENTRE secções (gap do flex).                       ║
// ║   • Section → a margem LATERAL (gutter). Nada mais a define.              ║
// ║   • SectionHeading → o espaço entre um rótulo e o seu conteúdo.           ║
// ║                                                                          ║
// ║ Um ecrã escreve estrutura, não medidas. É a diferença entre uma app com   ║
// ║ ritmo e uma app onde cada tela foi afinada à mão e diverge na seguinte.   ║
// ╚══════════════════════════════════════════════════════════════════════════╝

/** Contentor de uma página inteira. Dono do ritmo entre secções. */
export function Page({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`page-enter page-stack ${className}`.trim()}>{children}</div>;
}

/**
 * Uma secção da página. Dono do gutter lateral — e de mais nada.
 * `flush` = o conteúdo sangra até à margem do ecrã (trilhos horizontais, capas).
 */
export function Section({ children, flush = false, id, style, className = '' }: {
  children: ReactNode;
  flush?: boolean;
  id?: string;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <section id={id} style={style} className={`sect${flush ? ' flush' : ''} ${className}`.trim()}>
      {children}
    </section>
  );
}

/**
 * Cabeçalho de secção: rótulo (eyebrow) + título opcional + ação opcional.
 *
 * Existe porque o mesmo gesto — nomear um bloco — estava escrito de oito formas
 * («eyebrow» com `marginBottom` 3, 4, 6, 8 ou 10, com e sem `display:block`,
 * com e sem `padding:'0 2px'`), e no detalhe do evento o rótulo «Briefing»
 * estava DENTRO do card enquanto «Programa» e «Local» estavam por fora. Ler uma
 * página assim custa: o olho não aprende onde é que os títulos vivem.
 */
export function SectionHeading({ label, title, action, tone = 'purple' }: {
  label?: ReactNode;
  title?: ReactNode;
  action?: ReactNode;
  tone?: 'purple' | 'gold' | 'muted';
}) {
  return (
    <div className="sect-head">
      <div className="sect-heading">
        {label && <div className={`eyebrow${tone === 'purple' ? '' : ` ${tone}`}`}>{label}</div>}
        {title && <div className="sect-title">{title}</div>}
      </div>
      {action}
    </div>
  );
}

/** Pilha vertical de itens (listas de cards). Um só gap em toda a app. */
export function Stack({ children, tight = false, style }: {
  children: ReactNode;
  tight?: boolean;
  style?: CSSProperties;
}) {
  return <div className={`stack${tight ? ' tight' : ''}`} style={style}>{children}</div>;
}
